/**
 * React Query hooks for customer live event bookings.
 */

import {useQuery, useMutation} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {message} from "antd";
import {getCustomerLiveEventRepository} from "../data/repositories";
import {AnalyticsEvent} from "../../../utils/analytics";
import type {
  PaginatedCustomerLiveEventBookings,
  CustomerLiveEventPaginationParams,
  CustomerLiveEventJoinParams,
} from "../domain/types";
import type {JoinLiveEventResult} from "../../live-events/domain/types";

const QUERY_KEY = "customer-live-events";

type ApiError = AxiosError<{status: boolean; message: string}>;

interface UseCustomerUpcomingEventsOptions extends CustomerLiveEventPaginationParams {}

/**
 * Hook for fetching customer's upcoming live event bookings.
 *
 * @example
 * const { data, isLoading } = useCustomerUpcomingEvents({ page: 1, perPage: 5 });
 * // data.data = CustomerLiveEventBooking[]
 * // data.hasNextPage = boolean
 */
export const useCustomerUpcomingEvents = (
  options: UseCustomerUpcomingEventsOptions = {}
) => {
  const repository = getCustomerLiveEventRepository();

  return useQuery<PaginatedCustomerLiveEventBookings, ApiError>({
    queryKey: [QUERY_KEY, "upcoming", options],
    queryFn: () =>
      repository.getUpcoming({
        page: options.page,
        perPage: options.perPage,
      }),
    refetchInterval: 300000,
    keepPreviousData: true,
  });
};

interface UseCustomerPastEventsOptions extends CustomerLiveEventPaginationParams {}

/**
 * Hook for fetching customer's past live event bookings.
 *
 * @example
 * const { data, isLoading } = useCustomerPastEvents({ page: 1, perPage: 5 });
 */
export const useCustomerPastEvents = (options: UseCustomerPastEventsOptions = {}) => {
  const repository = getCustomerLiveEventRepository();

  return useQuery<PaginatedCustomerLiveEventBookings, ApiError>({
    queryKey: [QUERY_KEY, "past", options],
    queryFn: () =>
      repository.getPast({
        page: options.page,
        perPage: options.perPage,
      }),
    refetchInterval: 300000,
    keepPreviousData: true,
  });
};

/**
 * Hook for joining a live event as a customer.
 * Returns meeting credentials for joining the session.
 * Always uses the v2 API.
 *
 * @example
 * const { mutate, isLoading } = useJoinCustomerLiveEvent();
 * mutate({ eventId: 123, reference: "ref_abc" }, {
 *   onSuccess: (result) => {
 *     window.open(launchMeeting({ joinResult: result, ... }).url, "_blank");
 *   }
 * });
 */
export const useJoinCustomerLiveEvent = () => {
  const repository = getCustomerLiveEventRepository();

  return useMutation<JoinLiveEventResult, ApiError, CustomerLiveEventJoinParams>({
    mutationFn: (params) => repository.join(params),
    onError: (error) => {
      message.error(error.message);
      AnalyticsEvent("Booking", "Customer Live class booking join fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Booking", "Customer Live class booking join success");
    },
  });
};
